import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { ResultOptionDisclosure } from '../../general/Disclosure';
import { aow, pension, supplement, SUPPLEMENT_PER_MONTH_DIVIDER } from './Cocktail';
import { CocktailWrapperScreen } from './CocktailWrapperScreen';
import { SavingButtons } from './DialogueDataSaver';
import { AdditionalPension } from './DisclosureUnits/AdditionalPension';
import { ChangeExpenses } from './DisclosureUnits/ChangeExpenses';
import { ChangePensionAge } from './DisclosureUnits/ChangePensionAge';
import { OtherIncomes } from './DisclosureUnits/Otherincome';
import { OverageTopText } from './DisclosureUnits/OverageTopText';
import { SaveOrInvest } from './DisclosureUnits/SaveOrInvest';
import { ShortageTopText } from './DisclosureUnits/ShortageTopText';
import { SummaryDisclosure } from './DisclosureUnits/SummaryDisclosure';
import { BackButton } from './NavigationButtons';

export const ResultsSection: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: { general, personalInfo, expensesInfo, pensionInfo, supplementInfo },
    } = useContext(PensionContext);
    const totalAOW =
        aow(pensionInfo, personalInfo, general.taxPercentage ?? 0) +
        aow(pensionInfo, personalInfo, general.taxPercentage ?? 0, true);
    const totalEmployeePension =
        pension(pensionInfo, personalInfo, general.taxPercentage ?? 0) +
        pension(pensionInfo, personalInfo, general.taxPercentage ?? 0, true);

    const investments = supplement(supplementInfo, 'investment', false);
    const savings = supplement(supplementInfo, 'saving', false);
    const totalSupplement =
        Math.floor(investments / SUPPLEMENT_PER_MONTH_DIVIDER / 100) * 100 +
        Math.floor(savings / SUPPLEMENT_PER_MONTH_DIVIDER / 100) * 100;

    const total = totalAOW + totalEmployeePension + totalSupplement;
    let isGoalAchieved = false;
    let pensionGoal = 0;
    if (expensesInfo?.goal) {
        isGoalAchieved = expensesInfo.goal < total;
        pensionGoal = expensesInfo.goal;
    }
    
    // TODO: Changes to the values below, will influence the disclosure elements. 
    // Pay attention when implementing tickets: 3196
    let isGoalAchievedPPD = false; // TODO: Change this value dynamically (ticket 3196), PPD === PartnerPensionDate

    // TODO: Add computations for totals in different pension periods here
    const difference = Math.abs(pensionGoal - total);
    const differencePPD = Math.abs(pensionGoal - total); // TODO: Implement calculation (ticket 3196)

    const getGoalText = () =>
        isGoalAchieved 
            ? <OverageTopText goal={pensionGoal} />
            : <ShortageTopText difference={difference} />;

    const getDisclosureElements = () =>
        <>
            {isGoalAchieved 
                ? <>
                    <DisclosureWrapperChangePensionAge isGoalAchieved={isGoalAchieved} />
                    <DisclosureWrapperChangeExpenses />
                    <DisclosureWrapperAdditionalPension />
                    <DisclosureWrapperSaveOrInvest />
                </>
                : <>
                    <DisclosureWrapperAdditionalPension />
                    <DisclosureWrapperSaveOrInvest />
                    <DisclosureWrapperChangePensionAge isGoalAchieved={isGoalAchieved} />
                    <DisclosureWrapperChangeExpenses />
                </>}
            <DisclosureWrapperOtherIncomes />
            <DisclosureWrapperSummary 
                isGoalAchieved={isGoalAchieved}
                isGoalAchievedPPD={isGoalAchievedPPD}
                difference={difference}
                differencePPD={differencePPD}
            />
        </>

    return (
        <CocktailWrapperScreen
            page={5}
            isColorGoal
            title={t('pension-cocktail-fill-screen-results-title')}
            isResultsScreen={true}
        >
            {getGoalText()}
            {getDisclosureElements()}
            <div className="flex flex-row flex-wrap my-10 items-start">
                <BackButton className="" />
            </div>
            <SavingButtons/>
        </CocktailWrapperScreen>
    );
};

const DisclosureWrapperAdditionalPension: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const { parameters: { supplementInfo } } = useContext(PensionContext);
    const [hiddenStateAdditionalPension, setHiddenStateAdditionalPension] = useState<boolean>(true);
    
    const isCompleted = 
        (!!supplementInfo?.yearSpacePensionAccount?.monthlyPaymentAmount) 
            ? supplementInfo.yearSpacePensionAccount.monthlyPaymentAmount > 0
            : false;
    
    const title = t('pension-result-disclosure-additional-pension-title');
    const tooltip = {
        title: t('pension-result-disclosure-additional-pension-tooltip-title'),
        text: t('pension-result-disclosure-additional-pension-tooltip-text'),
        id: "tooltip_disclosure_additional_pension"
    };

    return (
        <ResultOptionDisclosure 
            title={title} 
            isCompleted={isCompleted}
            tooltip={tooltip} 
            hiddenState={hiddenStateAdditionalPension} 
            setHiddenState={setHiddenStateAdditionalPension}
        >
            <AdditionalPension 
                setHiddenState={setHiddenStateAdditionalPension}
            />
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperSaveOrInvest: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const { parameters: { supplementInfo } } = useContext(PensionContext);

    const isCompleted = 
        !!supplementInfo?.self?.additionalInvestmentAccount?.isUsed 
        || !!supplementInfo?.self?.additionalSavingAccount?.isUsed;

    const title = t('pension-result-disclosure-save-or-invest-title');;
    const tooltip = {
        title: t('pension-result-disclosure-save-or-invest-tooltip-title'),
        text: t('pension-result-disclosure-save-or-invest-tooltip-text'),
        id: "tooltip_disclosure_save_or_invest"
    };

    return (
        <ResultOptionDisclosure 
            title={title}
            isCompleted={isCompleted}
            tooltip={tooltip}
        >
            <SaveOrInvest />
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperChangeExpenses: React.FC<{}> = () => {
    const { t } = useTranslation('pension');

    const title = t('pension-result-disclosure-change-expenses-title');
    const tooltip = {
        title: t('pension-result-disclosure-change-expenses-tooltip-title'),
        text: t('pension-result-disclosure-change-expenses-tooltip-text'),
        id: "tooltip_disclosure_change_expenses"
    };

    return (
        <ResultOptionDisclosure 
            title={title}
            tooltip={tooltip}
        >
            <ChangeExpenses />
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperOtherIncomes: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const { parameters: { supplementInfo } } = useContext(PensionContext);
    
    const isCompleted = 
        (!!supplementInfo?.otherIncomeAvailable) 
        && ((!!supplementInfo?.otherIncome.gift) 
            || (!!supplementInfo?.otherIncome.heritage) 
            || (!!supplementInfo?.otherIncome.surplusHouse) 
            || (!!supplementInfo?.otherIncome.other));

    const title = t('pension-result-disclosure-other-income-title');
    const tooltip = {
        title: t('pension-result-disclosure-other-income-tooltip-title'),
        text: t('pension-result-disclosure-other-income-tooltip-text'),
        id: "tooltip_disclosure_other_incomes"
    };

    return (
        <ResultOptionDisclosure 
            title={title}
            isCompleted={isCompleted}
            tooltip={tooltip}
        >
            <OtherIncomes />
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperChangePensionAge: React.FC<{ isGoalAchieved: boolean }> = ({ isGoalAchieved=false }) => {
    const { t } = useTranslation('pension');
    const { parameters: { personalInfo } } = useContext(PensionContext);
    // TODO: Compare pension age to dynamic pension age (useGetPensionCalcData below), substitute for the static 67 value below.
    // const pensionCalcData = useGetPensionCalcData();

    const isCompleted = 
        personalInfo.self?.pensionAge !== 67 
        || ((!!personalInfo.isCalculatedTogether) && personalInfo.partner?.pensionAge !== 67);

    const title = isGoalAchieved
        ? t('pension-result-disclosure-change-pension-age-title-goal-achieved')
        : t('pension-result-disclosure-change-pension-age-title-goal-not-achieved');
    const tooltip = {
        title: t('pension-result-disclosure-change-pension-age-tooltip-title'),
        text: t('pension-result-disclosure-change-pension-age-tooltip-text'),
        id: "tooltip_disclosure_change_pension_age"
    };

    return (
        <ResultOptionDisclosure 
            title={title}
            isCompleted={isCompleted}
            tooltip={tooltip}
        >
            <ChangePensionAge 
                isGoalAchieved={isGoalAchieved} 
            />
        </ResultOptionDisclosure>
    );
};

const DisclosureWrapperSummary: React.FC<{ 
    isGoalAchieved: boolean, 
    isGoalAchievedPPD: boolean, 
    difference: number, 
    differencePPD: number }> 
= ({ isGoalAchieved, isGoalAchievedPPD, difference, differencePPD }) => {
    const { t } = useTranslation('pension');

    const title = t('pension-result-disclosure-summary-title');
    const tooltip = {
        title: t('pension-result-disclosure-summary-tooltip-title'),
        text: t('pension-result-disclosure-summary-tooltip-text'),
        id: "tooltip_disclosure_summary"
    };

    return (
        <ResultOptionDisclosure 
            title={title}
            tooltip={tooltip}
        >
            <SummaryDisclosure 
                isGoalAchieved={isGoalAchieved}
                isGoalAchievedPPD={isGoalAchievedPPD}
                difference={difference}
                differencePPD={differencePPD}
            />
        </ResultOptionDisclosure>
    );
};
